import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSessionStorage, setSessionStorage } from 'src/common/storage';
import { Area, KeyValue, Reservation, Table, TableItem } from 'src/pages/venues/types';
import { HostViewEnum, ResStatusEnum, resStatusToHostViewStatusMap, StatusEnum } from 'src/pages/venues/types/enums';

export enum ViewType {
	LIST = 'list',
	TABLES = 'tables',
}

export enum HostViewFiltersEnum {
	Date = 'date',
	Time = 'time',
	Location = 'location',
	Source = 'source',
	Upcoming = 'upcoming',
}

export interface OverviewDetails {
	waiTime: number;
	guests: number;
	availability: number;
	inUseTables: number;
	tables: number;
}

export interface HostViewFilters {
	[HostViewFiltersEnum.Date]: KeyValue | null;
	[HostViewFiltersEnum.Time]: KeyValue | null;
	[HostViewFiltersEnum.Location]: KeyValue | null;
	[HostViewFiltersEnum.Source]?: KeyValue | null;
	[HostViewFiltersEnum.Upcoming]?: KeyValue | null;
}

interface HostViewState {
	view: ViewType;
	width: number;
	searchQuery: string;
	openSidebar: boolean;
	mobileAssign: boolean;
	filters: HostViewFilters;
	activeTab: HostViewEnum;
	activeArea: Area | null;
	statuses: StatusEnum[];
	areas: Area[];
	reservations: Reservation[];
	upcomingReservations: Reservation[];
	assignReservation: string | null;
	assignTables: string[];
}

const sessionActiveBusiness = JSON.parse(getSessionStorage('act_bsn') || 'null');
const rawHostViewFilters = JSON.parse(getSessionStorage('host-view-filters') || 'null');
const hostFilterKeys = new Set(Object.values(HostViewFiltersEnum));

let sessionHostViewFiltersMap: Record<string, HostViewFilters> = {};

if (rawHostViewFilters) {
	const rawKeys = Object.keys(rawHostViewFilters);
	const isLegacyStructure = rawKeys.some((key) => hostFilterKeys.has(key as HostViewFiltersEnum));

	if (isLegacyStructure && sessionActiveBusiness?._id) {
		// Legacy storage stored a single filter object, migrate it to per-business storage.
		sessionHostViewFiltersMap[sessionActiveBusiness._id] = rawHostViewFilters;
	} else if (!isLegacyStructure) {
		sessionHostViewFiltersMap = rawHostViewFilters;
	}
}

const sessionHostViewFiltersForBusiness = sessionActiveBusiness?._id ? sessionHostViewFiltersMap[sessionActiveBusiness._id] : null;

const initialState: HostViewState = {
	view: ViewType.TABLES,
	statuses: [],
	searchQuery: '',
	activeArea: null,
	openSidebar: false,
	mobileAssign: false,
	width: window.innerWidth,
	activeTab: HostViewEnum.Reservation,
	filters: sessionHostViewFiltersForBusiness ?? {
		[HostViewFiltersEnum.Date]: { key: String(new Date()), value: String(new Date()) },
		[HostViewFiltersEnum.Time]: null,
		[HostViewFiltersEnum.Location]: null,
	},
	areas: [],
	reservations: [],
	assignTables: [],
	upcomingReservations: [],
	assignReservation: null,
};

const hostViewSlice = createSlice({
	name: 'hostView',
	initialState,
	reducers: {
		setSidebarOpen: (state) => {
			state.openSidebar = !state.openSidebar;
		},
		setSearchQuery: (state, action: PayloadAction<string>) => {
			state.searchQuery = action.payload;
		},
		setView: (state, action: PayloadAction<ViewType>) => {
			state.view = action.payload;
		},
		setMobileAssign: (state, action: PayloadAction<boolean>) => {
			state.mobileAssign = action.payload;
		},
		setWidth: (state, action: PayloadAction<number>) => {
			state.width = action.payload;
		},
		setStatuses: (state, action: PayloadAction<StatusEnum[]>) => {
			state.statuses = action.payload;
		},
		setActiveArea: (state, action: PayloadAction<Area | null>) => {
			state.activeArea = action.payload;
		},
		setActiveTab: (state, action: PayloadAction<HostViewEnum>) => {
			state.activeTab = action.payload;
		},
		setFilters: (state, action: PayloadAction<HostViewFilters>) => {
			const stringifiedJson = JSON.stringify(action.payload);

			if (JSON.stringify(state.filters) !== stringifiedJson) {
				const activeBusiness = JSON.parse(getSessionStorage('act_bsn') || 'null');
				const bid = activeBusiness?._id;

				if (bid) {
					const storedFiltersByBusiness = JSON.parse(getSessionStorage('host-view-filters') || '{}');
					const storedKeys = Object.keys(storedFiltersByBusiness || {});
					const storedIsLegacy = storedKeys.some((key) => hostFilterKeys.has(key as HostViewFiltersEnum));

					const nextFiltersByBusiness: Record<string, HostViewFilters> = storedIsLegacy ? {} : storedFiltersByBusiness || {};
					nextFiltersByBusiness[bid] = action.payload;
					setSessionStorage('host-view-filters', JSON.stringify(nextFiltersByBusiness));
				}
			}

			state.filters = action.payload;
		},
		setAreas: (state, action: PayloadAction<Area[]>) => {
			state.areas = action.payload;
		},
		setReservations: (state, action: PayloadAction<{ reservations: Reservation[]; id?: string }>) => {
			const { id, reservations } = action.payload;

			if (id) {
				state.reservations = state.reservations.map((reservation) =>
					reservation._id === id ? { ...reservation, ...reservations.find((r) => r._id === id) } : reservation
				);
				return;
			}

			state.reservations = reservations;
		},
		setUpcomingReservations: (state, action: PayloadAction<Reservation[]>) => {
			state.upcomingReservations = action.payload;
		},
		setAssignTables: (state, action: PayloadAction<string[]>) => {
			state.assignTables = action.payload;
		},
		setAssignReservation: (state, action: PayloadAction<string | null>) => {
			state.assignReservation = action.payload;
		},
	},
});

export const {
	setView,
	setWidth,
	setSearchQuery,
	setSidebarOpen,
	setActiveArea,
	setActiveTab,
	setStatuses,
	setFilters,
	setAreas,
	setMobileAssign,
	setReservations,
	setAssignTables,
	setAssignReservation,
	setUpcomingReservations,
} = hostViewSlice.actions;

export const selectView = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.view;
export const selectWidth = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.width;
export const selectFilters = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.filters;
export const selectStatuses = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.statuses;
export const selectActiveTab = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.activeTab;
export const selectActiveArea = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.activeArea;
export const selectOpenSidebar = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.openSidebar;
export const selectSearchQuery = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.searchQuery;
export const selectMobileAssign = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.mobileAssign;
export const selectReservations = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.reservations;
export const selectAssignTables = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.assignTables;
export const selectAssignReservation = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.assignReservation;
export const selectAreas = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.areas.filter((area) => area.bookable);
export const selectUpcomingReservations = (state: { venue: { hostView: HostViewState } }) => state.venue.hostView.upcomingReservations;

const filterReservations = createSelector([selectReservations, selectSearchQuery], (reservations, searchQuery) => {
	return reservations.filter((res) => {
		const { fname, lname, phone } = res.host;
		return (
			searchQuery.length < 3 ||
			(fname && fname.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(lname && lname.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(phone && phone.toLowerCase().includes(searchQuery.toLowerCase()))
		);
	});
});

export const selectUpcomingReserved = createSelector([filterReservations], (filteredReservations) => {
	const currentTime = new Date().getTime();

	const upcoming = filteredReservations
		.filter((reservation) => {
			const reservationDate = new Date(reservation.date.slice(0, -1));

			return (
				reservationDate.getTime() > currentTime &&
				(reservation.status === ResStatusEnum.Confirmed || reservation.status === ResStatusEnum.Pending)
			);
		})
		.sort((a, b) => {
			const timeA = new Date(a.date);
			const timeB = new Date(b.date);

			if (timeA.getTime() === timeB.getTime()) {
				return a.tables ? 1 : -1;
			}
			return timeA.getTime() - timeB.getTime();
		});

	const upcomingPeople = upcoming.reduce((sum, reservation) => sum + (reservation.people || 0), 0);

	return {
		upcoming,
		upcomingPeople,
		upcomingTables: upcoming.length,
	};
});

export const selectSeatedReserved = createSelector([filterReservations], (filteredReservations) => {
	const seated = filteredReservations
		.filter((reservation) => reservation.status === ResStatusEnum.Arrived)
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

	const seatedPeople = seated.reduce((sum, reservation) => sum + (reservation.people || 0), 0);

	return {
		seated,
		seatedPeople,
		seatedTables: seated.length,
	};
});

const selectActiveReservations = createSelector([selectReservations], (reservations) =>
	reservations.filter(
		(res) =>
			![
				ResStatusEnum.Completed,
				ResStatusEnum.Rejected,
				ResStatusEnum.NoShow,
				ResStatusEnum.Canceled,
				ResStatusEnum.StoreCanceled,
				ResStatusEnum.ClientCanceled,
			].includes(res.status)
	)
);

const selectReservedTablesMap = createSelector([selectActiveReservations], (activeReservations) => {
	const reservedTablesMap = new Map();

	activeReservations.forEach((res) => {
		res.tables.forEach((table) => {
			if (!reservedTablesMap.has(table._id)) {
				reservedTablesMap.set(table._id, []);
			}
			reservedTablesMap.get(table._id).push(res);
		});
	});

	return reservedTablesMap;
});

const sortByStatus = (a: Table, b: Table) => {
	const statusOrder = {
		[StatusEnum.Available]: 1,
		[StatusEnum.Reserved]: 2,
		[StatusEnum.Occupied]: 3,
	};
	return statusOrder[a.status] - statusOrder[b.status];
};

const sortByDate = (a: Reservation, b: Reservation) => {
	return new Date(a.date).getTime() - new Date(b.date).getTime();
};

export const selectTables = (disableFilters = false, disableArea = false, selectedArea = '') =>
	createSelector(
		[selectAreas, selectActiveArea, selectStatuses, selectReservedTablesMap, selectUpcomingReservations],
		(areas, activeArea, activeStatuses, reservedTablesMap, upcomingReservations) => {
			const updateTableStatus = (table: Table) => {
				const reservations = reservedTablesMap.get(table._id) || [];

				if (!reservations.length) {
					const tableUpcomingReservations = upcomingReservations.filter((reservation) =>
						reservation.tables.some((t) => t._id === table._id)
					);
					tableUpcomingReservations.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

					return {
						...table,
						date: tableUpcomingReservations[0]?.date,
						res_id: null,
						duration: null,
						preferences: null,
						guest: null,
						people: `${table.min_seats}-${table.max_seats}`,
						status: StatusEnum.Available,
					};
				}

				reservations.sort(sortByDate);
				const currentReservation = reservations[0];
				return {
					...table,
					date: currentReservation.date,
					res_id: currentReservation._id,
					people: currentReservation.people,
					duration: currentReservation.duration_mins,
					preferences: currentReservation.preferences,
					guest: `${currentReservation.host.fname} ${currentReservation.host.lname}`,
					status: resStatusToHostViewStatusMap[currentReservation.status] ?? StatusEnum.Reserved,
				};
			};

			const filterTableByBookableAndActiveStatus = (table) =>
				table.bookable && (disableFilters || !activeStatuses.length || activeStatuses.includes(table.status));

			const filterAndUpdateTables = (tables) => tables.map(updateTableStatus).filter(filterTableByBookableAndActiveStatus).sort(sortByStatus);

			const areaId = disableArea ? selectedArea : activeArea?._id;
			if (areaId) {
				const selectedTables = areas.find((area) => area._id === areaId)?.tables || [];
				return filterAndUpdateTables(selectedTables);
			}

			return areas.flatMap((area) => filterAndUpdateTables(area.tables));
		}
	);

export const selectStatusDetails = createSelector([selectTables(true, false)], (tables: Table[]) =>
	tables.reduce(
		(details, { status }) => {
			if (status === StatusEnum.Available) details.available += 1;
			else if (status === StatusEnum.Reserved) details.reserved += 1;
			else if (status === StatusEnum.Occupied) details.occupied += 1;

			return details;
		},
		{ available: 0, reserved: 0, occupied: 0 }
	)
);

export const selectOverviewDetails = createSelector(
	[selectTables(true, true), selectReservations],
	(tables: Table[], reservations: Reservation[]): OverviewDetails => {
		const details: OverviewDetails = {
			waiTime: 30,
			guests: 0,
			availability: 0,
			inUseTables: 0,
			tables: tables.length ?? 0,
		};

		const totalAvailableTables = tables.reduce(
			(acc, table) => {
				if (table.status === StatusEnum.Available) {
					acc.available++;
				} else {
					acc.inUse++;
					if (table.status === StatusEnum.Reserved || table.status === StatusEnum.Occupied) {
						acc.guests += table.people || 0;
					}
				}
				return acc;
			},
			{ available: 0, inUse: 0, guests: 0 }
		);

		details.inUseTables = totalAvailableTables.inUse;

		const inUseReservationIds = new Set(
			tables
				.filter((table) => table.status === StatusEnum.Reserved || table.status === StatusEnum.Occupied)
				.map((table) => table.res_id)
				.filter(Boolean)
		);
		details.guests = reservations.filter((r) => inUseReservationIds.has(r._id)).reduce((sum, r) => sum + (r.people || 0), 0);

		if (details.tables > 0) {
			details.availability = 100 - Math.round((totalAvailableTables.available / details.tables) * 100);
		}

		return details;
	}
);

export const selectAssignRes = createSelector([selectReservations, selectAssignReservation], (reservations, assignResId) => {
	const reservationToAssign = reservations.find((res) => res._id === assignResId);
	return reservationToAssign;
});

const isSameReservationTime = (resToAssign, reservedTable) => {
	if (!reservedTable) return false;
	const resStartTime = new Date(resToAssign.date).getTime();
	const reservedStartTime = new Date(reservedTable.date).getTime();
	return reservedStartTime === resStartTime;
};

export const selectIsTableAssignable = (tableId: string) =>
	createSelector(
		[selectAssignRes, selectAssignTables, selectReservedTablesMap, selectAreas, selectActiveArea],
		(resToAssign, assignTables, reservedTablesMap, areas, activeArea) => {
			if (!resToAssign) {
				return false;
			}
			if (assignTables.includes(tableId)) {
				return true;
			}
			if (isSameReservationTime(resToAssign, reservedTablesMap.get(tableId))) {
				// TODO: Number(resToAssign.duration_mins) * 60 * 1000
				return false;
			}

			const totalPeople =
				areas
					.find((area) => area._id === activeArea?._id)
					?.tables.reduce((acc, table) => (assignTables.includes(table._id) ? acc + table.max_seats : acc), 0) || 0;

			return resToAssign.people > totalPeople;
		}
	);

export const selectEnableSuccessAssignBtn = createSelector(
	[selectAreas, selectActiveArea, selectAssignRes, selectAssignTables],
	(areas, activeArea, resToAssign, assignTables) => {
		const selActiveArea = areas.find((area) => area._id === activeArea?._id);
		const tablesInActiveArea = selActiveArea?.tables.filter((table) => assignTables.includes(table._id));
		const maxSeatsInActiveArea = (tablesInActiveArea ?? []).reduce((total, table) => total + table.max_seats, 0);
		const minSeatsInActiveArea = (tablesInActiveArea ?? []).reduce((total, table) => total + table.min_seats, 0);

		const isAssignable = Boolean(resToAssign?.people) && (resToAssign?.people ?? 0) <= maxSeatsInActiveArea;

		let assignMessage;
		if (!((resToAssign?.people ?? 0) >= minSeatsInActiveArea && (resToAssign?.people ?? 0) <= maxSeatsInActiveArea)) {
			if ((resToAssign?.people ?? 0) >= maxSeatsInActiveArea) {
				assignMessage = 'Not enough seats selected';
			} else if ((resToAssign?.people ?? 0) < maxSeatsInActiveArea) {
				assignMessage = 'Table(s) seats exceed reserved';
			}
		}

		return {
			isAssignable,
			assignMessage,
		} as {
			isAssignable: boolean;
			assignMessage?: string;
		};
	}
);

export default hostViewSlice.reducer;
